import React, { useEffect, useState, useCallback } from 'react';
import Card from '../../components/ui/Card';
import Btn from '../../components/ui/Btn';
import Badge from '../../components/ui/Badge';
import ThFilter from '../../components/ui/ThFilter';
import PedidoDetail, { STATUS_TOM, STATUS_LABEL } from './PedidoDetail';
import { OPCOES_CATEGORIA } from './PropostaForm';
import { pedidosApi } from '../../api/vendas';
import { usuariosApi } from '../../api/admin';
import { COLORS } from '../../theme';
import { useAuth } from '../../context/AuthContext';

const CATEGORIA_LABEL = Object.fromEntries(OPCOES_CATEGORIA.filter(o => o.value).map(o => [o.value, o.label]));

const OPCOES_STATUS = [
  { value: 'enviado_almoxarifado', label: STATUS_LABEL.enviado_almoxarifado },
  { value: 'em_separacao', label: STATUS_LABEL.em_separacao },
  { value: 'aguardando_faturamento', label: STATUS_LABEL.aguardando_faturamento },
  { value: 'faturado', label: STATUS_LABEL.faturado },
  { value: 'devolvido', label: STATUS_LABEL.devolvido },
  { value: 'cancelado', label: STATUS_LABEL.cancelado }
];

const FILTROS_VAZIO = {
  numero: '', proposta_numero: '', numero_nota_fiscal: '', cliente_busca: '',
  vendedor_id: '', categoria: '', data: {}, valor: {}, status: '', empresa_id: ''
};

function formatarReais(v) {
  return (Number(v) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export default function PedidosList() {
  const { empresas } = useAuth();
  const [pedidos, setPedidos] = useState([]);
  const [vendedores, setVendedores] = useState([]);
  const [filtros, setFiltros] = useState(FILTROS_VAZIO);
  const [pedidoAberto, setPedidoAberto] = useState(undefined);

  const carregar = useCallback(() => {
    const params = {};
    if (filtros.numero) params.numero = filtros.numero;
    if (filtros.proposta_numero) params.proposta_numero = filtros.proposta_numero;
    if (filtros.numero_nota_fiscal) params.numero_nota_fiscal = filtros.numero_nota_fiscal;
    if (filtros.cliente_busca) params.cliente_busca = filtros.cliente_busca;
    if (filtros.vendedor_id) params.vendedor_id = filtros.vendedor_id;
    if (filtros.categoria) params.categoria = filtros.categoria;
    if (filtros.data.de) params.data_de = filtros.data.de;
    if (filtros.data.ate) params.data_ate = filtros.data.ate;
    if (filtros.valor.de) params.valor_min = filtros.valor.de;
    if (filtros.valor.ate) params.valor_max = filtros.valor.ate;
    if (filtros.status) params.status = filtros.status;
    if (filtros.empresa_id) params.empresa_id = filtros.empresa_id;
    pedidosApi.listar(params).then(setPedidos).catch(() => {});
  }, [filtros]);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => { usuariosApi.listarVendedores().then(setVendedores).catch(() => {}); }, []);

  const setFiltro = (campo, valor) => setFiltros(f => ({ ...f, [campo]: valor }));
  const opcoesVendedores = vendedores.map(v => ({ value: String(v.id), label: v.nome }));
  const opcoesEmpresas = empresas.map(e => ({ value: String(e.id), label: e.nome }));
  const algumFiltroAtivo = Object.entries(filtros).some(([, v]) => (typeof v === 'object' ? (v.de || v.ate) : v));

  if (pedidoAberto !== undefined) {
    return <PedidoDetail pedidoId={pedidoAberto} onVoltar={() => { setPedidoAberto(undefined); carregar(); }} />;
  }

  return (
    <div>
      <Card style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: 14, borderBottom: `1px solid ${COLORS.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {algumFiltroAtivo
            ? <Btn variante="ghost" onClick={() => setFiltros(FILTROS_VAZIO)}>Limpar Filtros</Btn>
            : <span style={{ fontSize: 12, color: COLORS.textLight }}>Clique no título de qualquer coluna pra filtrar</span>}
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr>
                <ThFilter label="Nº Pedido" tipo="numero" value={filtros.numero} onChange={v => setFiltro('numero', v)} />
                <ThFilter label="Empresa" tipo="select" options={opcoesEmpresas} value={filtros.empresa_id} onChange={v => setFiltro('empresa_id', v)} />
                <ThFilter label="Data" tipo="data" value={filtros.data} onChange={v => setFiltro('data', v)} />
                <ThFilter label="Categoria" tipo="select" options={OPCOES_CATEGORIA.filter(o => o.value)} value={filtros.categoria} onChange={v => setFiltro('categoria', v)} />
                <ThFilter label="Vendedor" tipo="select" options={opcoesVendedores} value={filtros.vendedor_id} onChange={v => setFiltro('vendedor_id', v)} />
                <ThFilter label="Status" tipo="select" options={OPCOES_STATUS} value={filtros.status} onChange={v => setFiltro('status', v)} />
                <ThFilter label="Nº Proposta" tipo="numero" value={filtros.proposta_numero} onChange={v => setFiltro('proposta_numero', v)} />
                <ThFilter label="Cliente" tipo="texto" value={filtros.cliente_busca} onChange={v => setFiltro('cliente_busca', v)} />
                <ThFilter label="Valor" tipo="valor" value={filtros.valor} onChange={v => setFiltro('valor', v)} align="right" />
                <ThFilter label="Nº NF" tipo="texto" value={filtros.numero_nota_fiscal} onChange={v => setFiltro('numero_nota_fiscal', v)} />
              </tr>
            </thead>
            <tbody>
              {pedidos.map(p => (
                <tr key={p.id} onClick={() => setPedidoAberto(p.id)}
                  style={{ borderTop: `1px solid ${COLORS.border}`, cursor: 'pointer' }}>
                  <td style={{ padding: 10 }}>#{p.numero}</td>
                  <td style={{ padding: 10 }}>{p.empresa_nome || '-'}</td>
                  <td style={{ padding: 10 }}>{new Date(p.criado_em).toLocaleDateString('pt-BR')}</td>
                  <td style={{ padding: 10 }}>{CATEGORIA_LABEL[p.categoria] || '-'}</td>
                  <td style={{ padding: 10 }}>{p.vendedor_nome}</td>
                  <td style={{ padding: 10 }}><Badge tom={STATUS_TOM[p.status]}>{STATUS_LABEL[p.status]}</Badge></td>
                  <td style={{ padding: 10 }}>{p.proposta_numero ? `#${p.proposta_numero}` : '-'}</td>
                  <td style={{ padding: 10 }}>{p.cliente_nome}</td>
                  <td style={{ padding: 10, textAlign: 'right' }}>{formatarReais(p.valor_total)}</td>
                  <td style={{ padding: 10 }}>{p.numero_nota_fiscal || '-'}</td>
                </tr>
              ))}
              {pedidos.length === 0 && (
                <tr><td colSpan={10} style={{ padding: 20, textAlign: 'center', color: COLORS.textMid }}>Nenhum pedido encontrado.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
